/**
 * featured-sort.js
 * ─────────────────────────────────────────────────────────────
 * Mueve los productos con tag "SHOWROOM" al inicio del grid
 * y les aplica la clase .featured-product para el estilo premium.
 *
 * INSTALACIÓN:
 *   1. Subir este archivo a Assets/ del tema en Shopify.
 *   2. En theme.liquid (antes de </body>), agregar:
 *      {{ 'featured-sort.js' | asset_url | script_tag }}
 *
 * SELECTORES — Si los productos no se reordenan, revisar estos valores.
 * El script prueba múltiples selectores comunes de temas Shopify.
 * Si ninguno funciona, inspeccionar el HTML del grid y actualizar
 * la variable PRODUCT_CARD_SELECTORS con el selector correcto.
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Selectores comunes de product cards en temas Shopify ──────────────────
  // El script itera estos hasta encontrar el que devuelve cards en la página.
  const PRODUCT_CARD_SELECTORS = [
    '.grid-product',           // Debut, Brooklyn, Express, Supply
    '.product-grid-item',      // Narrative, Boundless, Venture
    '.product-card',           // Dawn, Refresh, Sense
    '.grid__item .product',    // genérico
    '[data-product-id]',       // cualquier card con data attribute
    '.collection-grid__item',  // temas custom
  ];

  // ── Selectores del contenedor del grid ────────────────────────────────────
  const GRID_CONTAINER_SELECTORS = [
    '.grid.product-grid',
    '.collection-grid',
    '.product-grid',
    '.grid--uniform',
    '#product-grid',
    '.grid',
  ];

  // ─────────────────────────────────────────────────────────────────────────

  function init() {
    const config = window.FeaturedProductsConfig;

    // Si no hay configuración o no hay productos destacados, salir.
    if (!config || !config.enabled || !config.handles || config.handles.length === 0) {
      return;
    }

    const featuredHandles = config.handles;
    const labelText = config.labelText || '★ Featured';

    // Esperar a que el DOM esté listo y también al posible lazy-render del grid.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        runWithRetry(featuredHandles, labelText);
      });
    } else {
      runWithRetry(featuredHandles, labelText);
    }
  }

  /**
   * Reintenta la operación hasta 5 veces con delay creciente.
   * Necesario cuando el grid se renderiza después del DOMContentLoaded
   * (ej: temas con lazy sections o AJAX).
   */
  function runWithRetry(featuredHandles, labelText, attempt) {
    attempt = attempt || 1;
    const maxAttempts = 5;
    const delay = attempt * 200; // 200ms, 400ms, 600ms...

    const success = reorderAndDecorate(featuredHandles, labelText);

    if (!success && attempt < maxAttempts) {
      setTimeout(function () {
        runWithRetry(featuredHandles, labelText, attempt + 1);
      }, delay);
    }
  }

  /**
   * Encuentra las product cards, identifica las destacadas,
   * las mueve al principio y les agrega el badge.
   * Retorna true si encontró cards para procesar.
   */
  function reorderAndDecorate(featuredHandles, labelText) {
    // 1. Encontrar el contenedor del grid
    const gridContainer = findElement(GRID_CONTAINER_SELECTORS);
    if (!gridContainer) return false;

    // 2. Encontrar todas las product cards
    const allCards = findAllCards(gridContainer);
    if (!allCards || allCards.length === 0) return false;

    const featuredCards = [];
    const regularCards = [];

    allCards.forEach(function (card) {
      const handle = getProductHandle(card);
      if (handle && featuredHandles.includes(handle)) {
        card.classList.add('featured-product');
        addFeaturedBadge(card, labelText);
        featuredCards.push(card);
      } else {
        regularCards.push(card);
      }
    });

    // Si no hay destacados, no hay nada que reordenar.
    if (featuredCards.length === 0) return true;

    // 3. Reordenar: primero destacados, luego el resto.
    // Usamos un fragment para evitar reflows múltiples.
    const fragment = document.createDocumentFragment();

    featuredCards.forEach(function (card) {
      fragment.appendChild(card);
    });
    regularCards.forEach(function (card) {
      fragment.appendChild(card);
    });

    gridContainer.appendChild(fragment);

    // 4. Disparar evento custom por si el tema necesita re-inicializar algo.
    gridContainer.dispatchEvent(new CustomEvent('featured-products:reordered', {
      bubbles: true,
      detail: { featuredCount: featuredCards.length }
    }));

    return true;
  }

  /**
   * Extrae el handle del producto desde el href del link interno de la card.
   * Ejemplo: /collections/ropa/products/camiseta-premium → camiseta-premium
   */
  function getProductHandle(card) {
    // Opción 1: data attribute explícito
    if (card.dataset.handle) return card.dataset.handle;

    // Opción 2: parsear el href del primer link que apunte a /products/
    const link = card.querySelector('a[href*="/products/"]');
    if (link) {
      const match = link.getAttribute('href').match(/\/products\/([^?#\/]+)/);
      if (match) return match[1];
    }

    // Opción 3: data-product-id si el handle está ahí
    if (card.dataset.productHandle) return card.dataset.productHandle;

    return null;
  }

  /**
   * Agrega el badge "Destacado" a la card si no lo tiene ya.
   */
  function addFeaturedBadge(card, labelText) {
    if (card.querySelector('.featured-product__badge')) return;

    // Asegurarse de que la card tenga position relative para el badge absoluto
    const currentPos = window.getComputedStyle(card).position;
    if (currentPos === 'static') {
      card.style.position = 'relative';
    }

    const badge = document.createElement('div');
    badge.className = 'featured-product__badge';
    badge.setAttribute('aria-label', labelText);
    badge.innerHTML = '<span class="featured-product__badge-text">' + labelText + '</span>';

    // Insertar al principio de la card
    card.insertBefore(badge, card.firstChild);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function findElement(selectors) {
    for (let i = 0; i < selectors.length; i++) {
      const el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  function findAllCards(container) {
    for (let i = 0; i < PRODUCT_CARD_SELECTORS.length; i++) {
      const cards = container.querySelectorAll(PRODUCT_CARD_SELECTORS[i]);
      if (cards && cards.length > 0) {
        return Array.from(cards);
      }
    }
    return null;
  }

  // ── Soporte para AJAX / infinite scroll ──────────────────────────────────
  // Cuando el tema recarga el grid via AJAX (filtros, sort), re-ejecutar.

  document.addEventListener('collection:reloaded', reinitOnAjax);     // Shopify Plus common
  document.addEventListener('ajaxinate:after', reinitOnAjax);         // Ajaxinate plugin
  document.addEventListener('CollectionFilters:updated', reinitOnAjax); // Genérico

  function reinitOnAjax() {
    const config = window.FeaturedProductsConfig;
    if (config && config.enabled) {
      setTimeout(function () {
        runWithRetry(config.handles, config.labelText);
      }, 300);
    }
  }

  // ── Iniciar ───────────────────────────────────────────────────────────────
  init();

})();